import * as React from "react";
import {Helpers} from "../../utils/helpers";
import axios from "axios";
import {PopupMessage} from "../../utils/message";
import {Config} from "../../config/config";
import {Button, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Input, Paper, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography} from "@mui/material";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CachedIcon from '@mui/icons-material/Cached';
import RemoveIcon from '@mui/icons-material/Remove';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {v4 as uuidv4} from 'uuid';

enum DIALOG_TYPE {
    NONE = 0,
    ADD = 1,
    EDIT = 2,
}

const LIMITED_SIZE = 2 * 1024 * 1024; // 2MB

export class BannerManagement extends React.Component<any, any>{
    inputRef: any;
    constructor(props: any) {
        super(props);

        this.state = {
            loading: false,
            banners: [],
            shownDialog: DIALOG_TYPE.NONE,
            newBanner: null,
            dialog: {
                focusOn: null,
            },
            s3Images: [],
            showS3Images: false,
            s3ImageSource: "",
        }

        this.inputRef = React.createRef();
    }

    componentDidMount(): void {
        this.fectchBanners();
    }

    fectchBanners = async () => {
        this.setState({loading: true});
        const url = `${Config.getInstance.apiUrl}/api/banners`;
        const headers = {
            Authorization: Helpers.getSessionId(),
        }

        const response = await axios.get(url, {headers});

        this.setState({loading: false});

        if (response.status === 200) {
            const data = response.data;
            const banners = data.banners;
            const s3Images = data.s3Images;
            this.setState({banners, s3Images});
        } else {
            const message = response.data.message;
            console.log('fetchBanners error', message);
            PopupMessage.error("Failed to load banners from the API");
        }
    }

    // #region Logic
    convertDataUrlToBinaryArray = (dataUrl) => {
        const base64String = dataUrl.split(',')[1];
        const binaryString = atob(base64String);
        const binaryArray = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            binaryArray[i] = binaryString.charCodeAt(i);
        }

        return binaryArray;
    };

    convertDataUrlToByteArray(dataUrl) {
        const base64String = dataUrl.split(',')[1];
        const binaryString = atob(base64String);
        const byteArray = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }

        return byteArray;
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text);

        PopupMessage.success("Copied to clipboard");
    }

    updateImageUrl = async (bannerId: string, imageUrl: string) => {
        if (bannerId != "") {
            const {banners} = this.state;
            const banner = banners.find((item) => item.id === bannerId);

            if (!banner) {
                PopupMessage.error("Banner not found");
                return;
            }
            banner.imageUrl = imageUrl;
            this.setState({banners})
        } else {
            let object = this.state.newBanner;
            object.imageUrl = imageUrl;
            this.setState({newBanner: object});
        }

        this.hideS3Images();
    }

    // #endregion

    // #region Dialog

    showAddBannerDialog = () => {
        this.setState({shownDialog: DIALOG_TYPE.ADD});
    }

    hideAddBannerDialog = () => {
        this.fectchBanners();
        this.setState({shownDialog: DIALOG_TYPE.NONE});
    }

    showBannerDetail = (bannerId: string) => {
        const {dialog} = this.state;
        dialog.focusOn = bannerId;

        this.setState({dialog, shownDialog: DIALOG_TYPE.EDIT});
    }

    hideBannerDetail = () => {
        const {dialog} = this.state;
        dialog.focusOn = null;
        this.fectchBanners();
        this.setState({dialog, shownDialog: DIALOG_TYPE.NONE});
    }

    showS3Images = (source = "") => {
        this.setState({showS3Images: true, s3ImageSource: source});
    }

    hideS3Images = () => {
        this.setState({showS3Images: false, s3ImageSource: ""});
    }

    // #endregion

    // #region Handlers
    onDeleteBanner = async (id: string) => {
        if (confirm("Are you sure to delete this banner?")) {
            const url = `${Config.getInstance.apiUrl}/api/banners/delete`;
            const headers = {
                Authorization: Helpers.getSessionId(),
            }
            const body = {
                id
            }

            try {
                const response = await axios.post(url, body, {headers});

                const success = response.status === 200 && response.data.success;
                if (success) {
                    PopupMessage.success("Banner deleted successfully");
                    this.fectchBanners();
                } else {
                    const message = response.data.message;
                    console.log('onDeleteBanner error', message);
                    PopupMessage.error("Failed to delete banner to the API");
                }
            } catch (e) {
                console.log('onDeleteBanner error', e);
                PopupMessage.error("Failed to delete banner to the API");
            }
        }
    }

    onNewBannerChange = (e: any, key: string) => {
        const {newBanner} = this.state;

        if (key === "isActive") {
            // True, false
            newBanner[key] = e.target.checked;
        } else {
            newBanner[key] = e.target.value;
        }

        this.setState({newBanner});
    }

    onEditBannerChange = (e: any, key: string) => {
        const {dialog, banners} = this.state;
        const bannerId = dialog.focusOn;
        const found = banners.find((item) => item.id === bannerId);

        if (!found) {
            PopupMessage.error("Banner not found");
            return;
        }

        if (key === "isActive") {
            // True, false
            found[key] = e.target.checked;
        } else if (key === "id") {
            // Store to new property
            const newId = e.target.value;
            found["newId"] = newId;
        } else {
            found[key] = e.target.value;
        }

        this.setState({dialog});
    }

    onUploadPress = async () => {
        this.inputRef.click();
    }

    onImageChange = (e: any, object: any, name = "newBanner") => {
        const file = e.target.files[0];

        if (file) {
            // Check file size
            if (file.size > LIMITED_SIZE) {
                PopupMessage.error("File size is too large");
                return;
            }

            const reader = new FileReader();

            reader.onloadend = () => {
                const result = reader.result;
                object.image = result;
                object.imageUrl = "";
                if (name === "newBanner") {
                    this.setState({newBanner: object});
                } else {
                    const {banners} = this.state;
                    this.setState({banners})
                }

            };

            reader.readAsDataURL(file);
        }
    }

    onRemoveImageOnNewBanner = () => {
        const {newBanner} = this.state;
        newBanner.image = "";
        this.setState({newBanner});
    }

    onUploadBanner = async () => {
        const {newBanner} = this.state;
        const url = `${Config.getInstance.apiUrl}/api/banners/upload`;
        const headers = {
            Authorization: Helpers.getSessionId(),
        }

        const body = {
            description: newBanner.description,
            image: newBanner.image,
            isActive: newBanner.isActive,
            imageUrl: newBanner.imageUrl,
        }

        // const element = document.createElement("a");
        // const fileName = "body.txt";
        // const blob = new Blob([JSON.stringify(body)], {type: "text/plain"});
        // element.href = URL.createObjectURL(blob);
        // element.download = fileName;
        // document.body.appendChild(element); // Required for this to work in FireFox
        // element.click();

        try {
            const response = await axios.post(url, body, {headers});

            const success = response.status === 200 && response.data.success;
            if (success) {
                PopupMessage.success("Banner added successfully");
                this.hideAddBannerDialog();
            } else {
                const message = response.data.message;
                console.log('onAddBanner error', message);
                PopupMessage.error("Failed to add banner to the API");
            }
        } catch (e) {
            console.log('onAddBanner error', e);
            PopupMessage.error("Failed to add banner to the API");
        }

    }

    onEditBanner = async () => {
        const {banners, dialog} = this.state;
        const bannerId = dialog.focusOn;

        const banner = banners.find((item) => item.id === bannerId);

        if (!banner) {
            PopupMessage.error("Banner not found");
            return;
        }

        const url = `${Config.getInstance.apiUrl}/api/banners/update`;
        const headers = {
            Authorization: Helpers.getSessionId(),
        }
        const body = {
            id: banner.id,
            newId: banner.newId ? banner.newId : banner.id,
            description: banner.description,
            image: banner.image,
            imageUrl: banner.imageUrl,
            isActive: banner.isActive
        }

        try {
            const response = await axios.post(url, body, {headers});

            const success = response.status === 200 && response.data.success;
            if (success) {
                PopupMessage.success("Banner edited successfully");
                this.hideBannerDetail();
            } else {
                const message = response.data.message;
                console.log('onEditBanner error', message);
                PopupMessage.error("Failed to edit banner to the API");
            }
        } catch (e) {
            console.log('onEditBanner error', e);
            PopupMessage.error("Failed to edit banner to the API");
        }
    }

    onRemoveImageOnEditBanner = () => {
        const {dialog, banners} = this.state;
        const bannerId = dialog.focusOn;
        const found = banners.find((item) => item.id === bannerId);

        if (!found) {
            PopupMessage.error("Banner not found");
            return;
        }

        found.image = "";
        found.imageUrl = "";
        this.setState({dialog});
    }

    onRenewId = async () => {
        const {dialog, banners} = this.state;
        const bannerId = dialog.focusOn;
        const found = banners.find((item) => item.id === bannerId);

        if (!found) {
            PopupMessage.error("Banner not found");
            return;
        }

        const newId = uuidv4();
        found.newId = newId;

        this.setState({banners});
    }

    // #endregion

    // #region Render
    renderIsActive = (isActive: boolean) => {
        let color = "grey"
        let text = "Inactive"

        if (isActive) {
            color = "green";
            text = "Active";
        }

        return <Typography><span style={{color}}>{text}</span></Typography>
    }

    renderS3Images() {
        const {s3Images, s3ImageSource} = this.state;

        if (s3Images.length === 0) {
            return <div className="no-items-placeholder">
                <NotInterestedIcon />
                <h3>No Images found</h3>
            </div>;
        }

        return <Dialog
            open={this.state.showS3Images}
            onClose={this.hideS3Images}
        >
            <DialogTitle>
                Choose Image from S3
            </DialogTitle>
            <DialogContent dividers>
                {
                    s3Images.map((image, index) => {
                        return <img
                            key={index}
                            src={image}
                            alt="banner"
                            style={{width: "100%", marginBottom: 10}}
                            onClick={() => this.updateImageUrl(s3ImageSource, image)}
                        />
                    })
                }
            </DialogContent>
        </Dialog>
    }

    renderAddBannerDialog() {
        const textStyle = {
            display: "flex",
            alignItems: "center",
        }

        let {newBanner} = this.state;
        if (!newBanner) {
            newBanner = {
                description: "",
                image: "",
                isActive: false,
                imageUrl: "",
            }

            this.setState({newBanner});
        }

        return <Dialog
            open={this.state.shownDialog === DIALOG_TYPE.ADD}
            onClose={this.hideAddBannerDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth={true}
            maxWidth="lg"
        >
            <DialogTitle>
                Add Banner
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Description:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <TextField
                                fullWidth
                                value={newBanner.description}
                                onChange={(e) => this.onNewBannerChange(e, "description")}
                            />
                        </Grid>
                    </Grid>
                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Image URL:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <TextField
                                fullWidth
                                value={newBanner.imageUrl}
                                disabled
                            />
                        </Grid>
                    </Grid>

                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Image:
                            </Typography>
                        </Grid>
                        <Grid item container sm={9} spacing={2}>
                            <Grid item sm={2}>
                                <Button variant="contained" color="primary" onClick={() => this.showS3Images()}>
                                    <TouchAppIcon />
                                    Pick
                                </Button>
                            </Grid>
                            <Grid item sm={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => this.onUploadPress()}
                                >
                                    <AddIcon />
                                    Upload
                                </Button>
                                <Input
                                    type="file"
                                    inputRef={(input) => (this.inputRef = input)}
                                    inputProps={{accept: 'image/png'}}
                                    onChange={(e) => this.onImageChange(e, newBanner)}
                                    style={{opacity: 0, position: 'absolute', pointerEvents: 'none'}}
                                />
                            </Grid>

                            {
                                (newBanner.image || newBanner.imageUrl) && <div>
                                    <img src={newBanner.image ? newBanner.image : newBanner.imageUrl} alt="banner" style={{width: "100%"}} />
                                    <Button variant="contained" color="secondary" onClick={this.onRemoveImageOnNewBanner}>
                                        Remove
                                    </Button>

                                </div>
                            }
                        </Grid>
                    </Grid>

                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Active:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <Switch
                                checked={newBanner.isActive}
                                onChange={(e) => this.onNewBannerChange(e, "isActive")}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={this.hideAddBannerDialog} color="error" variant="contained">
                    Cancel
                </Button>
                <Button onClick={this.onUploadBanner} color="primary" variant="contained" autoFocus>
                    Add
                </Button>
            </DialogActions>

        </Dialog>
    }

    renderEditBannerDialog() {
        const textStyle = {
            display: "flex",
            alignItems: "center",
        }

        const middleContentStyle = {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }

        const {dialog, banners} = this.state;
        const bannerId = dialog.focusOn;

        const found = banners.find((item) => item.id === bannerId);
        if (!found) {
            // PopupMessage.error("Banner not found");
            return null;
        }

        return <Dialog
            open={this.state.shownDialog === DIALOG_TYPE.EDIT}
            onClose={this.hideAddBannerDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth={true}
            maxWidth="lg"
        >
            <DialogTitle>
                Edit Banner
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Id:
                            </Typography>
                        </Grid>
                        <Grid item sm={7}>
                            <TextField
                                fullWidth
                                value={!found.newId ? found.id : found.newId}
                                onChange={(e) => this.onEditBannerChange(e, "id")}
                            />
                        </Grid>
                        <Grid item sm={2} style={middleContentStyle}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CachedIcon />}
                                onClick={() => this.onRenewId()}
                            >
                                Renew
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Description:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <TextField
                                fullWidth
                                value={found.description}
                                onChange={(e) => this.onEditBannerChange(e, "description")}
                            />
                        </Grid>
                    </Grid>

                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Image URL:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <TextField
                                fullWidth
                                value={found.imageUrl}
                                disabled
                            />
                        </Grid>
                    </Grid>

                    <Grid container item sm={12}>

                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Image:
                            </Typography>
                        </Grid>
                        <Grid item container sm={9} spacing={2}>
                            <Grid item sm={2}>
                                <Button variant="contained" color="primary" onClick={() => this.showS3Images(found.id)}>
                                    <TouchAppIcon />
                                    Pick
                                </Button>
                            </Grid>
                            <Grid item sm={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => this.onUploadPress()}
                                >
                                    <AddIcon />
                                    Upload
                                </Button>
                                <Input
                                    type="file"
                                    inputRef={(input) => (this.inputRef = input)}
                                    inputProps={{accept: 'image/png'}}
                                    onChange={(e) => this.onImageChange(e, found, "banner")}
                                    style={{opacity: 0, position: 'absolute', pointerEvents: 'none'}}
                                />
                            </Grid>

                            {
                                (found.imageUrl || found.image) && <div>
                                    <img src={found.imageUrl ? found.imageUrl : found.image} alt="banner" style={{width: "100%"}} />
                                    <Button variant="contained" color="secondary" onClick={this.onRemoveImageOnEditBanner}>
                                        Remove
                                    </Button>

                                </div>
                            }
                        </Grid>

                        {/* <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Image:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => this.onUploadPress()}
                            >
                                <AddIcon />
                                Upload
                            </Button>
                            <Input
                                type="file"
                                inputRef={(input) => (this.inputRef = input)}
                                inputProps={{accept: 'image/png'}}
                                onChange={(e) => this.onImageChange(e, found, "banner")}
                                style={{opacity: 0, position: 'absolute', pointerEvents: 'none'}}
                            />
                            {
                                (found.imageUrl || found.image) && <div>
                                    <img src={found.imageUrl ? found.imageUrl : found.image} alt="banner" style={{width: "100%"}} />
                                    <Button variant="contained" color="secondary" onClick={this.onRemoveImageOnEditBanner}>
                                        Remove
                                    </Button>

                                </div>
                            }
                        </Grid> */}
                    </Grid>

                    <Grid container item sm={12}>
                        <Grid item sm={2} style={textStyle}>
                            <Typography variant="h6">
                                Active:
                            </Typography>
                        </Grid>
                        <Grid item sm={9}>
                            <Switch
                                checked={found.isActive}
                                onChange={(e) => this.onEditBannerChange(e, "isActive")}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={this.hideBannerDetail} color="error" variant="contained">
                    Cancel
                </Button>
                <Button onClick={this.onEditBanner} color="primary" variant="contained" autoFocus>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    }

    renderBanners() {
        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={11}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        const {banners} = this.state;

        if (!this.state.loading && banners.length === 0) {
            return <div className="no-items-placeholder">
                <NotInterestedIcon />
                <h3>No Banners found</h3>
            </div>;
        }

        return <React.Fragment>
            <Table className="min-table-size">
                <TableHead>
                    <TableRow>
                        <TableCell style={{width: 70}}>BannerId</TableCell>
                        <TableCell style={{width: 70}}>Description</TableCell>
                        <TableCell style={{width: 70}}>isActive</TableCell>
                        <TableCell style={{width: 50}} align="center">Preview</TableCell>
                        <TableCell style={{width: 50}} align="center"></TableCell>
                        <TableCell style={{width: 20}} align="center"></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        banners.map((banner, index) => {
                            return <TableRow key={index}>
                                <TableCell>{!banner.newId ? banner.id : banner.newId}</TableCell>
                                <TableCell>{banner.description}</TableCell>
                                <TableCell>{this.renderIsActive(banner.isActive)}</TableCell>
                                <TableCell align="center">
                                    <img src={banner.imageUrl} alt="banner" style={{width: "100%"}} />
                                </TableCell>
                                <TableCell align="center">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => this.showBannerDetail(banner.id)}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                                <TableCell align="center" style={{width: 20}}>
                                    <IconButton
                                        style={{borderRadius: '50%', backgroundColor: 'red'}}
                                        onClick={() => this.onDeleteBanner(banner.id)}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        })
                    }
                </TableBody>
            </Table>
        </React.Fragment>
    }

    render() {
        return <div className="contentPadding" style={{width: "100%"}}>
            {this.renderAddBannerDialog()}
            {this.renderEditBannerDialog()}
            {this.renderS3Images()}

            <CardHeader title="Banner Managerment" />
            <Paper className="root" elevation={0}>
                <Button
                    variant="contained"
                    color="primary"
                    style={{float: 'right'}}
                    onClick={this.showAddBannerDialog}
                >
                    <AddIcon />
                    Add
                </Button>
                {this.renderBanners()}
            </Paper>
        </div>;
    }

    // #endregion
}